import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMainPageTitle } from '../../contexts/PageContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import SendRequestForm from '../../components/player/parents/SendRequestForm';
import ReceivedRequestsList from '../../components/player/parents/ReceivedRequestsList';
import SentRequestsList from '../../components/player/parents/SentRequestsList';
import ParentsList from '../../components/player/parents/ParentsList';
import {
  useAllParentsOfPlayer,
  useAllSentRequests,
  useAllReceivedRequests,
  useSendRequestToAddParent,
  useDeleteSentRequest,
  useApproveOrRejectRequest,
  useRemoveParent
} from '../../hooks/useParent';

const Parents = () => {
  const { t } = useTranslation();
  const { updatePageTitle } = useMainPageTitle();
  const [activeMainTab, setActiveMainTab] = useState('requests');
  const [activeRequestTab, setActiveRequestTab] = useState('received');
  const [searchUsername, setSearchUsername] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]);

  useEffect(() => {
    updatePageTitle(t('player.parents.title'), t('player.parents.description'));
  }, [updatePageTitle, t]);

  // Data fetching hooks
  const { data: parentsData, isLoading: parentsLoading, error: parentsError } = useAllParentsOfPlayer();
  const { data: sentRequestsData, isLoading: sentLoading, error: sentError } = useAllSentRequests();
  const { data: receivedRequestsData, isLoading: receivedLoading, error: receivedError } = useAllReceivedRequests();

  // Mutation hooks
  const sendRequestMutation = useSendRequestToAddParent();
  const deleteSentRequestMutation = useDeleteSentRequest();
  const approveRejectMutation = useApproveOrRejectRequest();
  const removeParentMutation = useRemoveParent();

  // Extract data arrays
  const parents = parentsData?.data || [];
  const sentRequests = sentRequestsData?.data || [];
  const receivedRequests = receivedRequestsData?.data || [];
  
  // Loading and error states
  const isLoading = parentsLoading || sentLoading || receivedLoading;
  const error = parentsError || sentError || receivedError;

  // Event handlers
  const handleSendRequest = async (e) => {
    e.preventDefault();
    if (!searchUsername.trim() || selectedRoles.length === 0) return;
    
    try {
      await sendRequestMutation.mutateAsync({
        parentUsername: searchUsername,
        roles: selectedRoles
      });
      setSearchUsername('');
      setSelectedRoles([]);
    } catch (error) {
      console.error('Error sending parent request:', error);
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      await approveRejectMutation.mutateAsync({
        requestId,
        status: 'approved'
      });
    } catch (error) {
      console.error('Error approving request:', error);
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await approveRejectMutation.mutateAsync({
        requestId,
        status: 'rejected'
      });
    } catch (error) {
      console.error('Error rejecting request:', error);
    }
  };

  const handleDeleteSentRequest = async (requestId) => {
    try {
      await deleteSentRequestMutation.mutateAsync(requestId);
    } catch (error) {
      console.error('Error deleting sent request:', error);
    }
  };

  const handleRemoveParent = async (parentId) => {
    if (!window.confirm(t('player.parents.confirmRemoveParent'))) return;
    
    try {
      await removeParentMutation.mutateAsync(parentId);
    } catch (error) {
      console.error('Error removing parent:', error);
    }
  };

  if (isLoading) {
    return <LoadingSpinner message={t('player.parents.loadingParentInfo')} />;
  }

  if (error) {
    return <ErrorMessage message={t('player.parents.failedToLoadParentInfo')} />;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('player.parents.parentManagement')}</h1>
        <p className="text-gray-600">{t('player.parents.manageParentRelationships')}</p>
      </div>

      {/* Main Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveMainTab('requests')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeMainTab === 'requests'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('player.parents.requests')}
            </button>
            <button
              onClick={() => setActiveMainTab('all-parents')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeMainTab === 'all-parents'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('player.parents.allParents')}
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Requests Tab Content */}
          {activeMainTab === 'requests' && (
            <div>
              {/* Send Request Form */}
              <SendRequestForm
                searchUsername={searchUsername}
                setSearchUsername={setSearchUsername}
                selectedRoles={selectedRoles}
                setSelectedRoles={setSelectedRoles}
                onSubmit={handleSendRequest}
                isLoading={sendRequestMutation.isPending}
              />

              {/* Request Sub-tabs */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="flex space-x-8">
                  <button
                    onClick={() => setActiveRequestTab('received')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeRequestTab === 'received'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {t('player.parents.receivedRequests')}
                  </button>
                  <button
                    onClick={() => setActiveRequestTab('sent')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeRequestTab === 'sent'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {t('player.parents.sentRequests')}
                  </button>
                </nav>
              </div>

              {/* Request Lists */}
              {activeRequestTab === 'received' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('player.parents.receivedRequests')}</h3>
                  <ReceivedRequestsList
                    requests={receivedRequests}
                    onApprove={handleApproveRequest}
                    onReject={handleRejectRequest}
                    isProcessing={approveRejectMutation.isPending}
                  />
                </div>
              )}

              {activeRequestTab === 'sent' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('player.parents.sentRequests')}</h3>
                  <SentRequestsList
                    requests={sentRequests}
                    onCancel={handleDeleteSentRequest}
                    isProcessing={deleteSentRequestMutation.isPending}
                  />
                </div>
              )}
            </div>
          )}

          {/* All Parents Tab Content */}
          {activeMainTab === 'all-parents' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('player.parents.allParents')}</h3>
              <ParentsList
                parents={parents}
                onRemove={handleRemoveParent}
                isRemoving={removeParentMutation.isPending}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Parents;